import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellFontName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellFont';
import {
    MeshCellTextAlign, MeshCellTextWrapping,
    MeshCellVertAlign
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {
    DefaultMeshCellSettings,
    IMeshCellSettings,
    MeshCellSettings,
    MeshCellSettingsProps
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {
    CellSettingsBarBorderAction,
    CellSettingsBarMenu
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {WSPIcons} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {CellSettingsBarButton} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/header/cellSettingsBar/CellSettingsBarButton';
import {CellSettingsSubMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/header/cellSettingsBar/CellSettingsSubMenu';
import {FontName} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/header/cellSettingsBar/FontName';
import {FontSize} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/header/cellSettingsBar/FontSize';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {ColorPicker} from '@mintdata/mint-kit/impl/components/colorPicker/ColorPicker';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import ResizeSensor from 'css-element-queries/src/ResizeSensor';

const styles = require('./CellSettingsBar.less');
const cx = classnames.bind(styles);

export interface ICellSettingsBarProps
{

}

export interface ICellSettingsBarState
{
    isTextAlignSubMenuVisible:boolean;
    isVertAlignSubMenuVisible:boolean;
    isTextWrappingSubMenuVisible:boolean;
    isBorderColorPickerVisible:boolean;
    isMoreMenuVisible:boolean;
}

enum Blocks
{
    FormatCopy = 'formatCopy',
    Formats = 'formats',
    FontFamilyAndSize = 'fontFamilyAndSize',
    FontStyle = 'fontStyle',
    BgAndBorders = 'bgAndBorders',
    TextOptions = 'textOptions',
}

const SEPARATOR_WIDTH:number = 14;
const BUTTON_WIDTH:number = 28;
const BUTTON_WITH_ARROW_WIDTH:number = 43;
const FORMAT_COPY_WIDTH:number = 37;
const FONT_FAMILY_WIDTH:number = 108;
const FONT_SIZE_WIDTH:number = 70;

const BLOCKS_WIDTH:Map<Blocks, number> = new Map<Blocks, number>();
BLOCKS_WIDTH.set(Blocks.FormatCopy, FORMAT_COPY_WIDTH + SEPARATOR_WIDTH);
BLOCKS_WIDTH.set(Blocks.Formats, 4 * BUTTON_WIDTH + SEPARATOR_WIDTH);
BLOCKS_WIDTH.set(Blocks.FontFamilyAndSize, FONT_FAMILY_WIDTH + FONT_SIZE_WIDTH + SEPARATOR_WIDTH);
BLOCKS_WIDTH.set(Blocks.FontStyle, 5 * BUTTON_WIDTH + SEPARATOR_WIDTH);
BLOCKS_WIDTH.set(Blocks.BgAndBorders, 2 * BUTTON_WIDTH + SEPARATOR_WIDTH);
BLOCKS_WIDTH.set(Blocks.TextOptions, 2 * BUTTON_WITH_ARROW_WIDTH + BUTTON_WIDTH);

@observer
export class CellSettingsBar extends BaseComponentWithContext<ICellSettingsBarProps, ICellSettingsBarState>
{
    private _elementRef:HTMLDivElement | null = null;
    private _resizeSensor:ResizeSensor;

    @observable
    private _width:number = 0;

    constructor(props)
    {
        super(props);

        this.state = {
            isTextAlignSubMenuVisible: false,
            isVertAlignSubMenuVisible: false,
            isTextWrappingSubMenuVisible: false,
            isBorderColorPickerVisible: false,
            isMoreMenuVisible: false,
        };
    }

    public componentWillUnmount():void
    {
        if( this._resizeSensor )
        {
            this._resizeSensor.detach(this._onContentResize);
            this._resizeSensor = null;
        }
    }

    @bind
    private _onElementRef(elementRef:HTMLDivElement):void
    {
        this._elementRef = elementRef;

        this._onContentResize();

        if( this._elementRef )
        {
            this._resizeSensor = new ResizeSensor(this._elementRef, this._onContentResize);
        }
    }

    @bind
    private _onContentResize():void
    {
        requestAnimationFrame(action(() =>
        {
            if( this._elementRef )
            {
                this._width = this._elementRef.clientWidth;
            }
        }));
    }

    private _handleEvent = (event?:React.MouseEvent<HTMLElement>, closePopover:boolean = true) =>
    {
        if( event )
        {
            event.stopPropagation();
            event.preventDefault();
        }

        this._closePopover(closePopover);

        return this._focusedSheetId;
    };

    private _closePopover = (closePopover:boolean = true) =>
    {
        if( !closePopover ) return;

        const {popupManager} = this.context;

        popupManager.onNonPopupItemClicked();

        this._focusMesh();
    };

    private _focusMesh(withTimeout?:boolean)
    {
        if( !this._focusedMeshCtx ) return;

        const {mesh} = controllers.ui.spreadsheets;

        if( withTimeout )
            setTimeout(() => mesh.focusMeshOrControl(this._focusedMeshCtx), 0);
        else
            mesh.focusMeshOrControl(this._focusedMeshCtx);
    }

    private _paintFormatModeBtnClick = (event:React.MouseEvent<HTMLElement>) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.cellSettingsBar.togglePaintFormatMode();
    };

    private _borderTypeDoubleBtnClick = (event:React.MouseEvent<HTMLElement>) =>
    {
        if( !this._handleEvent(event, false) ) return;

        controllers.ui.spreadsheets.cellSettingsBar.toggleBorderType();
    };

    private _setIsMenuOpened = (cellSettingsBarMenu:CellSettingsBarMenu) =>
    {
        const {isMenuOpened: wasOpened} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        controllers.ui.spreadsheets.cellSettingsBar.setIsMenuOpened(cellSettingsBarMenu);

        if( cellSettingsBarMenu ) return;

        let {tmpValue} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        if( wasOpened )
        {
            let prop:MeshCellSettingsProps;

            switch( wasOpened )
            {
                case CellSettingsBarMenu.FontName:
                    prop = MeshCellSettingsProps.FontName;
                    break;
                case CellSettingsBarMenu.FontSize:
                    prop = MeshCellSettingsProps.FontSize;
                    break;
                case CellSettingsBarMenu.TextColor:
                    if( tmpValue === '' ) tmpValue = null;
                    prop = MeshCellSettingsProps.TextColor;
                    break;
                case CellSettingsBarMenu.BgColor:
                    if( tmpValue === '' ) tmpValue = null;
                    prop = MeshCellSettingsProps.BgColor;
                    break;
            }

            if( prop )
                controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, prop as any, tmpValue);
        }

        if( wasOpened == CellSettingsBarMenu.Border )
            controllers.ui.spreadsheets.cellSettingsBar.setSelectedBorderAction(null);
    };

    private _fontNameOnChange = (fontName:MeshCellFontName, closePopover:boolean = true) =>
    {
        if( !this._handleEvent(null, false) ) return;

        if( !fontName ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.FontName as any, fontName);

        this._closePopover(closePopover);
    };

    private _fontSizeOnChange = (fontSize:number, closePopover:boolean = true) =>
    {
        if( !this._handleEvent(null, false) ) return;

        if( fontSize === null || fontSize === undefined ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.FontSize as any, fontSize);

        this._closePopover(closePopover);
    };

    @bind
    private _toggleCellProperty<T extends keyof IMeshCellSettings>(event:React.MouseEvent<HTMLElement>, prop:T)
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.meshCellSettings.toggleProperty(this._focusedMeshCtx, prop);
    };

    private _textColorOnChange = (textColor:string, closePopover:boolean = true) =>
    {
        if( !this._handleEvent(null, false) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.TextColor as any, textColor);

        this._closePopover(closePopover);
    };

    private _bgColorOnChange = (bgColor:string, closePopover:boolean = true) =>
    {
        if( !this._handleEvent(null, false) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.BgColor as any, bgColor !== null ? bgColor : '');

        this._closePopover(closePopover);
    };

    private _borderColorOnChange = (color:string) =>
    {
        if( !this._handleEvent(null, false) ) return;

        controllers.ui.spreadsheets.cellSettingsBar.setBorderColor(color);
    };

    private _textAlignBtnClick = (event:React.MouseEvent<HTMLElement>, textAlign:MeshCellTextAlign | null) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.TextAlign as any, textAlign);
    };

    private _vertAlignBtnClick = (event:React.MouseEvent<HTMLElement>, vertAlign:MeshCellVertAlign | null) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.VertAlign as any, vertAlign);
    };

    private _wrapTextBtnClick = (event:React.MouseEvent<HTMLElement>, textWrapping:MeshCellTextWrapping) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedMeshCtx, MeshCellSettingsProps.WrapText as any, textWrapping);
        // controllers.ui.spreadsheets.meshCellSettings.toggleProperty(this._focusedMeshCtx, MeshCellSettingsProps.WrapText as any);
    };

    private _borderSideBtnClick = (event:React.MouseEvent<HTMLElement>, side:CellSettingsBarBorderAction) =>
    {
        if( !this._handleEvent(event, false) ) return;

        controllers.ui.spreadsheets.cellSettingsBar.setSelectedBorderAction(side);
        controllers.ui.spreadsheets.meshCellSettings.setBorder(this._focusedMeshCtx, side);
    };

    private _valueFormatBtnClick = (event:React.MouseEvent<HTMLElement>, valueFormat:IMeshCellValueFormat) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        controllers.ui.spreadsheets.meshCellSettings.setValueFormat(this._focusedMeshCtx, valueFormat);
    };

    private _digitsAfterPointBtnClick = (event:React.MouseEvent<HTMLElement>, delta:-1 | 1) =>
    {
        if( !this._handleEvent(event, !this.state.isMoreMenuVisible) ) return;

        // controllers.ui.spreadsheets.meshCellSettings.setProperty(this._focusedSheetId, MeshCellSettingsProps.DigitsAfterPoint, delta);

        controllers.ui.spreadsheets.meshCellSettings.setDigitsAfterPoint(this._focusedMeshCtx, delta);
    };

    public render():JSX.Element
    {
        if( !this._focusedMeshState )
            return null;

        const {isMoreMenuVisible} = this.state;
        const cellSettingsPanelWidth:number = this._width;

        let panelBlocks:Blocks[] = [];
        let moreButtonBlocks:Blocks[] = [];
        let sum:number = BUTTON_WIDTH; // More button width
        let count:number = BLOCKS_WIDTH.size;

        for( let entry of Array.from(BLOCKS_WIDTH.entries()) )
        {
            count--;
            sum += entry[1];

            if( count === 0 )
                sum -= BUTTON_WIDTH;

            if( sum < cellSettingsPanelWidth )
                panelBlocks.push(entry[0]);
            else
                moreButtonBlocks.push(entry[0]);
        }

        return (
            <div
                ref={this._onElementRef}
                className={cx('base')}
            >
                {this._focusedSheetId && panelBlocks.map((block:Blocks) => this._renderBlocks(block))}

                {this._focusedSheetId && moreButtonBlocks.length > 0 && <CellSettingsBarButton
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.More}}
                    isToggledOn={isMoreMenuVisible}
                >
                    <CellSettingsSubMenu
                        focusedSheetId={this._focusedSheetId}
                        onShow={() => this.setState({isMoreMenuVisible: true}, this.forceUpdate)}
                        onHide={() =>
                        {
                            this.setState({isMoreMenuVisible: false});
                            this._focusMesh(true);
                        }}
                    >

                        <div className={cx('subMenuRow', 'moreMenu')}>
                            {moreButtonBlocks.map((block:Blocks) => this._renderBlocks(block))}
                        </div>
                    </CellSettingsSubMenu>
                </CellSettingsBarButton>}
            </div>
        );
    }

    private _renderBlocks = (block:Blocks) =>
    {
        switch( block )
        {
            case Blocks.FormatCopy:
                return this._renderFormatCopyBlock();
            case Blocks.Formats:
                return this.renderFormatsBlock();
            case Blocks.FontFamilyAndSize:
                return this.renderFontFamilyAndSizeBlock();
            case Blocks.FontStyle:
                return this.renderFontStyleBlock();
            case Blocks.BgAndBorders:
                return this.renderBgAndBordersBlock();
            case Blocks.TextOptions:
                return this._renderTextOptionsBlock();
        }

        return null;
    };

    private _renderFormatCopyBlock = () =>
    {
        const {isMoreMenuVisible} = this.state;
        const {isPaintFormatMode} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        return (
            <div key={Blocks.FormatCopy} className={cx('block')}>
                <CellSettingsBarButton
                    className={cx('btnFormatPaint', {btn: isMoreMenuVisible})}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatPaint}}
                    onClick={this._paintFormatModeBtnClick}
                    isToggledOn={isPaintFormatMode}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />

                {this._renderSeparator()}
            </div>
        );
    };

    private renderFormatsBlock = () =>
    {
        const {isMoreMenuVisible} = this.state;

        return (
            <div key={Blocks.Formats} className={cx('block')}>
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.CurrencyUsd}}
                    onClick={(e) => this._valueFormatBtnClick(e, MeshCellValueFormat.Currency)}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.Percent}}
                    onClick={(e) => this._valueFormatBtnClick(e, MeshCellValueFormat.Percent)}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.DecimalDecrease}}
                    onClick={(e) => this._digitsAfterPointBtnClick(e, -1)}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.DecimalIncrease}}
                    onClick={(e) => this._digitsAfterPointBtnClick(e, 1)}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />

                {this._renderSeparator()}
            </div>
        );
    };

    private renderFontFamilyAndSizeBlock = () =>
    {
        const {isMoreMenuVisible} = this.state;
        const {
            isMenuOpened,
            tmpValue
        } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        const settings:MeshCellSettings = this._getSettings();
        let fontName;
        let fontSize;

        if( isMenuOpened == CellSettingsBarMenu.FontName && tmpValue !== null )
            fontName = tmpValue;
        else if( settings && settings.fontName )
            fontName = settings.fontName;
        else
            fontName = DefaultMeshCellSettings[MeshCellSettingsProps.FontName];

        if( isMenuOpened == CellSettingsBarMenu.FontSize && tmpValue !== null )
            fontSize = tmpValue;
        else if( settings && (settings.fontSize || settings.fontSize === 0) )
            fontSize = settings.fontSize;
        else
            fontSize = DefaultMeshCellSettings[MeshCellSettingsProps.FontSize];

        return (
            <div key={Blocks.FontFamilyAndSize} className={cx('block')}>
                <CellSettingsBarButton
                    className={cx('btnFontName', {btn: isMoreMenuVisible})}
                    arrowIcon={true}
                    text={fontName}
                    isToggledOn={isMenuOpened == CellSettingsBarMenu.FontName}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    <FontName
                        focusedSheetId={this._focusedSheetId}
                        selected={fontName}
                        onChange={this._fontNameOnChange}
                        onShow={() => this._setIsMenuOpened(CellSettingsBarMenu.FontName)}
                        onHide={() =>
                        {
                            this._setIsMenuOpened(null);
                            this._focusMesh(true);
                        }}
                    />
                </CellSettingsBarButton>

                <CellSettingsBarButton
                    className={cx('btnFontSize', {btn: isMoreMenuVisible})}
                    arrowIcon={true}
                    isToggledOn={isMenuOpened == CellSettingsBarMenu.FontSize}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    <FontSize
                        focusedSheetId={this._focusedSheetId}
                        selected={fontSize}
                        isSelected={isMenuOpened == CellSettingsBarMenu.FontSize}
                        onChange={this._fontSizeOnChange}
                        onShow={() => this._setIsMenuOpened(CellSettingsBarMenu.FontSize)}
                        onHide={() =>
                        {
                            this._setIsMenuOpened(null);
                            this._focusMesh(true);
                        }}
                    />
                </CellSettingsBarButton>

                {this._renderSeparator()}
            </div>
        );
    };

    private renderFontStyleBlock = () =>
    {
        const {isMoreMenuVisible} = this.state;
        const {
            isMenuOpened,
            tmpValue
        } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        const settings:MeshCellSettings = this._getSettings();

        let isTextBold = false;
        let isTextItalic = false;
        let isTextUnderline = false;
        let isTextStrikethrough = false;

        let textColor;

        if( settings )
        {
            isTextBold = settings.textBold;
            isTextItalic = settings.textItalic;
            isTextUnderline = settings.textUnderline;
            isTextStrikethrough = settings.textStrikethrough;
        }

        if( isMenuOpened == CellSettingsBarMenu.TextColor && tmpValue !== null )
            textColor = tmpValue;
        else if( settings && settings.textColor )
            textColor = settings.textColor;
        else
            textColor = DefaultMeshCellSettings[MeshCellSettingsProps.TextColor];

        return (
            <div key={Blocks.FontStyle} className={cx('block')}>
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatBold, className: cx('fixVert')}}
                    onClick={(e) => this._toggleCellProperty(e, MeshCellSettingsProps.TextBold as any)}
                    isToggledOn={isTextBold}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatItalic, className: cx('fixVert')}}
                    onClick={(e) => this._toggleCellProperty(e, MeshCellSettingsProps.TextItalic as any)}
                    isToggledOn={isTextItalic}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatUnderline, className: cx('fixVert')}}
                    onClick={(e) => this._toggleCellProperty(e, MeshCellSettingsProps.TextUnderline as any)}
                    isToggledOn={isTextUnderline}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{
                        name: WSPIcons.visualDesigner.cellSettings.FormatStrikethrough,
                        className: cx('iconStrike')
                    }}
                    onClick={(e) => this._toggleCellProperty(e, MeshCellSettingsProps.TextStrikethrough as any)}
                    isToggledOn={isTextStrikethrough}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                />

                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatColorText}}
                    isToggledOn={isMenuOpened == CellSettingsBarMenu.TextColor}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    <div
                        className={cx('colorSwatch')}
                        style={{background: textColor}}
                    />
                    <ColorPicker
                        onChange={this._textColorOnChange}
                        color={textColor}
                        onShow={() => this._setIsMenuOpened(CellSettingsBarMenu.TextColor)}
                        onHide={(closedByButton?:boolean) =>
                        {
                            setTimeout(() => this._setIsMenuOpened(null), 0);
                            this._focusMesh(closedByButton);
                        }}
                    />
                </CellSettingsBarButton>

                {this._renderSeparator()}
            </div>
        );
    };

    private renderBgAndBordersBlock = () =>
    {
        const {isMoreMenuVisible} = this.state;
        const {
            isMenuOpened,
            tmpValue
        } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        const settings:MeshCellSettings = this._getSettings();
        let bgColor;

        if( isMenuOpened == CellSettingsBarMenu.BgColor && tmpValue !== null )
            bgColor = tmpValue;
        else if( settings && settings.bgColor )
            bgColor = settings.bgColor;
        else
            bgColor = DefaultMeshCellSettings[MeshCellSettingsProps.BgColor];

        return (
            <div key={Blocks.BgAndBorders} className={cx('block')}>
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatColorFill}}
                    isToggledOn={isMenuOpened == CellSettingsBarMenu.BgColor}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    <div
                        className={cx('colorSwatch')}
                        style={{background: bgColor}}
                    />
                    <ColorPicker
                        onChange={this._bgColorOnChange}
                        color={bgColor}
                        onShow={() => this._setIsMenuOpened(CellSettingsBarMenu.BgColor)}
                        onHide={(closedByButton?:boolean) =>
                        {
                            setTimeout(() => this._setIsMenuOpened(null), 0);
                            this._focusMesh(closedByButton);
                        }}
                    />
                </CellSettingsBarButton>

                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderAll}}
                    isToggledOn={isMenuOpened == CellSettingsBarMenu.Border}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    {this._renderBordersSubMenu()}
                </CellSettingsBarButton>

                {this._renderSeparator()}
            </div>
        );
    };

    private _renderTextOptionsBlock = () =>
    {
        const {
            isTextAlignSubMenuVisible,
            isVertAlignSubMenuVisible,
            isTextWrappingSubMenuVisible,
            isMoreMenuVisible
        } = this.state;

        let isTextAlignLeft = false;
        let isTextAlignRight = false;
        let isTextAlignCenter = false;

        let isVertAlignTop = false;
        let isVertAlignCenter = false;
        let isVertAlignBottom = false;

        let isTextWrappingDefault = false;
        let isTextWrappingWrap = false;
        let isTextWrappingClip = false;

        const settings:MeshCellSettings = this._getSettings();

        let horizontalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatAlignLeft;
        let verticalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignTop;
        let textWrappingIcon = WSPIcons.visualDesigner.cellSettings.TextWrappingDefault;

        if( settings )
        {
            if( settings.textAlign )
            {
                isTextAlignLeft = settings.textAlign == MeshCellTextAlign.Left;
                isTextAlignRight = settings.textAlign == MeshCellTextAlign.Right;
                isTextAlignCenter = settings.textAlign == MeshCellTextAlign.Center;
            }

            if( settings.vertAlign )
            {
                isVertAlignTop = settings.vertAlign == MeshCellVertAlign.Top;
                isVertAlignCenter = settings.vertAlign == MeshCellVertAlign.Center;
                isVertAlignBottom = settings.vertAlign == MeshCellVertAlign.Bottom;
            }

            if( settings.wrapText )
            {
                isTextWrappingDefault = settings.wrapText == MeshCellTextWrapping.Default;
                isTextWrappingWrap = settings.wrapText == MeshCellTextWrapping.Wrap;
                isTextWrappingClip = settings.wrapText == MeshCellTextWrapping.Clip;
            }

            if( isTextAlignLeft ) horizontalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatAlignLeft;
            if( isTextAlignRight ) horizontalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatAlignRight;
            if( isTextAlignCenter ) horizontalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatAlignCenter;

            if( isVertAlignTop ) verticalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignTop;
            if( isVertAlignBottom ) verticalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignBottom;
            if( isVertAlignCenter ) verticalAlignIcon = WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignCenter;

            if( isTextWrappingDefault ) textWrappingIcon = WSPIcons.visualDesigner.cellSettings.TextWrappingDefault;
            if( isTextWrappingWrap ) textWrappingIcon = WSPIcons.visualDesigner.cellSettings.TextWrappingWrap;
            if( isTextWrappingClip ) textWrappingIcon = WSPIcons.visualDesigner.cellSettings.TextWrappingClip;
        }

        return (
            <div key={Blocks.TextOptions} className={cx('block')}>
                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: horizontalAlignIcon}}
                    arrowIcon={true}
                    isToggledOn={isTextAlignSubMenuVisible}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    {this._renderAlignSubMenu()}
                </CellSettingsBarButton>

                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: verticalAlignIcon}}
                    arrowIcon={true}
                    isToggledOn={isVertAlignSubMenuVisible}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    {this._renderVertAlignSubMenu()}
                </CellSettingsBarButton>

                <CellSettingsBarButton
                    className={isMoreMenuVisible ? cx('btn') : null}
                    iconProps={{name: textWrappingIcon}}
                    arrowIcon={true}
                    isToggledOn={isTextWrappingSubMenuVisible}
                    isToggledOnClass={isMoreMenuVisible ? cx('subMenuBtnToggled') : null}
                >
                    {this._renderTextWrappingSubMenu()}
                </CellSettingsBarButton>
            </div>
        );
    };

    private _renderSeparator = (className:string = '') => <div className={cx('separator', className)} />;

    private _renderBordersSubMenu = () =>
    {
        const {
            borderColor,
            isBorderTypeDouble,
            selectedBorderAction
        } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        const {isBorderColorPickerVisible} = this.state;

        return (
            <CellSettingsSubMenu
                focusedSheetId={this._focusedSheetId}
                onShow={() => this._setIsMenuOpened(CellSettingsBarMenu.Border)}
                onHide={() =>
                {
                    this._setIsMenuOpened(null);
                    this._focusMesh(true);
                }}
            >

                <div className={cx('subMenuRow')}>

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderAll}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.All)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.All}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderInside}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Inside)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Inside}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderHorizontal}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Horizontal)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Horizontal}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderVertical}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Vertical)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Vertical}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderOutside}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Outside)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Outside}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    {this._renderSeparator('top')}

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderColor}}
                        isToggledOn={isBorderColorPickerVisible}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    >
                        <div
                            className={cx('colorSwatch')}
                            style={{background: borderColor}}
                        />
                        <ColorPicker
                            onChange={this._borderColorOnChange}
                            color={borderColor}
                            onShow={() => this.setState({isBorderColorPickerVisible: true})}
                            onHide={() => this.setState({isBorderColorPickerVisible: false})}
                        />
                    </CellSettingsBarButton>
                </div>

                <div className={cx('subMenuRow')}>

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderLeft}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Left)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Left}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderTop}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Top)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Top}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderRight}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Right)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Right}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderBottom}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.Bottom)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.Bottom}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderNone}}
                        onClick={(e) => this._borderSideBtnClick(e, CellSettingsBarBorderAction.None)}
                        isToggledOn={selectedBorderAction === CellSettingsBarBorderAction.None}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                    {this._renderSeparator('bottom')}

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.BorderBottomDouble}}
                        onClick={this._borderTypeDoubleBtnClick}
                        isToggledOn={isBorderTypeDouble}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                </div>

            </CellSettingsSubMenu>
        );
    };

    private _renderAlignSubMenu = () =>
    {
        let isTextAlignLeft = false;
        let isTextAlignRight = false;
        let isTextAlignCenter = false;

        const settings:MeshCellSettings = this._getSettings();

        if( settings && settings.textAlign )
        {
            isTextAlignLeft = settings.textAlign == MeshCellTextAlign.Left;
            isTextAlignRight = settings.textAlign == MeshCellTextAlign.Right;
            isTextAlignCenter = settings.textAlign == MeshCellTextAlign.Center;
        }

        return (
            <CellSettingsSubMenu
                focusedSheetId={this._focusedSheetId}
                onShow={() => this.setState({isTextAlignSubMenuVisible: true})}
                onHide={() =>
                {
                    this.setState({isTextAlignSubMenuVisible: false});
                    this._focusMesh(true);
                }}
            >

                <div className={cx('subMenuRow')}>

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatAlignLeft}}
                        onClick={(e) => this._textAlignBtnClick(e, MeshCellTextAlign.Left)}
                        isToggledOn={isTextAlignLeft}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatAlignCenter}}
                        onClick={(e) => this._textAlignBtnClick(e, MeshCellTextAlign.Center)}
                        isToggledOn={isTextAlignCenter}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatAlignRight}}
                        onClick={(e) => this._textAlignBtnClick(e, MeshCellTextAlign.Right)}
                        isToggledOn={isTextAlignRight}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                </div>

            </CellSettingsSubMenu>
        );
    };

    private _renderVertAlignSubMenu = () =>
    {
        let isVertAlignTop = false;
        let isVertAlignBottom = false;
        let isVertAlignCenter = false;

        const settings:MeshCellSettings = this._getSettings();

        if( settings && settings.vertAlign )
        {
            isVertAlignTop = settings.vertAlign == MeshCellVertAlign.Top;
            isVertAlignBottom = settings.vertAlign == MeshCellVertAlign.Bottom;
            isVertAlignCenter = settings.vertAlign == MeshCellVertAlign.Center;
        }

        return (
            <CellSettingsSubMenu
                focusedSheetId={this._focusedSheetId}
                onShow={() => this.setState({isVertAlignSubMenuVisible: true})}
                onHide={() =>
                {
                    this.setState({isVertAlignSubMenuVisible: false});
                    this._focusMesh(true);
                }}
            >

                <div className={cx('subMenuRow')}>

                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignTop}}
                        onClick={(e) => this._vertAlignBtnClick(e, MeshCellVertAlign.Top)}
                        isToggledOn={isVertAlignTop}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignCenter}}
                        onClick={(e) => this._vertAlignBtnClick(e, MeshCellVertAlign.Center)}
                        isToggledOn={isVertAlignCenter}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.FormatVerticalAlignBottom}}
                        onClick={(e) => this._vertAlignBtnClick(e, MeshCellVertAlign.Bottom)}
                        isToggledOn={isVertAlignBottom}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />

                </div>

            </CellSettingsSubMenu>
        );
    };

    private _renderTextWrappingSubMenu = () =>
    {
        let isTextWrappingDefault = false;
        let isTextWrappingWrap = false;
        let isTextWrappingClip = false;

        const settings:MeshCellSettings = this._getSettings();


        if( settings && settings.wrapText )
        {
            isTextWrappingDefault = settings.wrapText == MeshCellTextWrapping.Default;
            isTextWrappingWrap = settings.wrapText == MeshCellTextWrapping.Wrap;
            isTextWrappingClip = settings.wrapText == MeshCellTextWrapping.Clip;
        }

        return (
            <CellSettingsSubMenu
                focusedSheetId={this._focusedSheetId}
                onShow={() => this.setState({isTextWrappingSubMenuVisible: true})}
                onHide={() =>
                {
                    this.setState({isTextWrappingSubMenuVisible: false});
                    this._focusMesh(true);
                }}
            >
                <div className={cx('subMenuRow')}>
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.TextWrappingDefault}}
                        onClick={(e) => this._wrapTextBtnClick(e, MeshCellTextWrapping.Default)}
                        isToggledOn={isTextWrappingDefault}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.TextWrappingWrap}}
                        onClick={(e) => this._wrapTextBtnClick(e, MeshCellTextWrapping.Wrap)}
                        isToggledOn={isTextWrappingWrap}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                    <CellSettingsBarButton
                        className={cx('btn')}
                        iconProps={{name: WSPIcons.visualDesigner.cellSettings.TextWrappingClip}}
                        onClick={(e) => this._wrapTextBtnClick(e, MeshCellTextWrapping.Clip)}
                        isToggledOn={isTextWrappingClip}
                        isToggledOnClass={cx('subMenuBtnToggled')}
                    />
                </div>
            </CellSettingsSubMenu>
        );
    };

    private _getSettings():MeshCellSettings
    {
        if( !this._focusedMeshCtx )
            return null;

        const {sheetSettingsCtrl} = this._focusedMeshCtx;
        const {spreadsheets} = controllers.ui;

        const meshState = spreadsheets.mesh.getState(this._focusedMeshCtx);
        const activeCell = meshState.details.activeCell;

        return sheetSettingsCtrl.getCellSettings(this._focusedSheetId, activeCell.asKey());
    }

    @computed
    private get _focusedSheetId():SheetId
    {
        const {main} = controllers.ui.spreadsheets;

        const workbook = main.getRootWorkbook();
        if( !workbook ) return;

        return workbook.view.focusedSheetId;
    }

    @computed
    private get _focusedMeshCtx():IWSPMeshContext
    {
        return controllers.ui.spreadsheets.workbook.getMeshContextBySheetId(this._focusedSheetId);
    }

    @computed
    private get _focusedMeshState():MeshState
    {
        return this._focusedMeshCtx && controllers.ui.spreadsheets.mesh.getState(this._focusedMeshCtx);
    }
}
